import { type InMemoryCache } from '@apollo/client';
import type { ReadFieldFunction } from '@apollo/client/cache/core/types/common';

import { sendErrorEvent } from '@trello/error-reporting';

import type {
  TrelloCustomField,
  TrelloCustomFieldItemValueInfo,
} from '../generated';
import { type TrelloCustomFieldItem } from '../generated';
import {
  isBool,
  isNumber,
  isObjectId,
  isString,
  nullOrBool,
  nullOrNumber,
  nullOrString,
} from '../plannerCardDataMapping/validateHelpers';
import type { RecursivePartial, TargetModel } from './cacheModelTypes';
import { InvalidIdError } from './cacheSyncingErrors';
import { getObjectIdFromCacheObject } from './getObjectIdFromCacheObject';
import { syncNativeNestedObjectToRest } from './syncNativeNestedObjectToRest';
import { syncNestedModelReference } from './syncNestedModelReference';

/* For syncing dropdown custom fields */
const dropdownValueFieldMappings = {
  // In this case, the schema is not great and 'id' doesn't mean ARI but objectId :/
  id: { validate: (val: unknown) => val === null || isObjectId(val) },
};

const generateDropdownCustomFieldItemValueFragment = (field: string) => {
  return `fragment CustomFieldItemValueWrite on CustomFieldItem {
    id
    idValue
    value
  }`;
};

const generateDropdownCustomFieldItemValueData = (
  id: string,
  field: string,
  value: unknown,
) => {
  return {
    __typename: 'CustomFieldItem',
    id,
    idValue: value,
    value: null,
  };
};

/* For syncing non-dropdown custom fields */
const valueFieldMappings = {
  checked: {
    validate: nullOrBool,
    transform: (val: boolean | null) => (isBool(val) ? val.toString() : val),
  },
  date: { validate: nullOrString },
  number: {
    validate: nullOrNumber,
    transform: (val: number | null) => (isNumber(val) ? val.toString() : val),
  },
  text: { validate: nullOrString },
  // For non-dropdown custom fields, value.id should always be null
  id: {
    validate: (val: unknown) => val === null,
    generateFragment:
      () => `fragment CustomFieldItemValueidValueWrite on CustomFieldItem {
      id
      idValue
    }`,
    generateData: (id: string, value: unknown) => ({
      __typename: 'CustomFieldItem',
      id,
      idValue: value,
    }),
  },
};

const generateCustomFieldItemValueFragment = (field: string) => {
  return `fragment CustomFieldItemValue${field}Write on CustomFieldItem {
    id
    value {
      ${field}
    }
  }`;
};

const generateCustomFieldItemValueData = (
  id: string,
  field: string,
  value: unknown,
) => {
  return {
    __typename: 'CustomFieldItem',
    id,
    value: {
      __typename: 'CustomFieldItem_Value',
      [field]: value,
    },
  };
};

/**
 * Given native TrelloCustomFieldItem data, writes all custom
 * field item data to the CustomFieldItem model in the Apollo Cache
 * @param incoming A partial TrelloBoard model
 * @param cache The cache to write to
 * @param readField A function to read fields from cache references
 */
export const syncTrelloCustomFieldItemToCustomFieldItem = (
  incoming: RecursivePartial<TrelloCustomFieldItem>,
  cache: InMemoryCache,
  readField: ReadFieldFunction,
) => {
  try {
    const customFieldItemId = getObjectIdFromCacheObject(incoming, readField);
    const customFieldItem: TargetModel = {
      type: 'CustomFieldItem',
      id: customFieldItemId,
    };

    const customField = readField<TrelloCustomField>('customField', incoming);
    if (customField) {
      const customFieldId = readField<string>('objectId', customField);

      /**
       * It appears that in some cases the customField corresponding to the
       * incoming customFieldItem may not already be synced, leading to
       * an undefined value for the objectId field. In this case, we do not
       * want to sync the customField.idCustomField. However after the
       * customField is synced, the customFieldItem cache syncing appears
       * to run again and then the customField.idCustomField is defined.
       */
      if (customFieldId && !isObjectId(customFieldId)) {
        throw new InvalidIdError('TrelloCustomField', customFieldId);
      } else if (customFieldId) {
        syncNestedModelReference(
          customFieldItem,
          {
            model: {
              id: customFieldId,
              type: 'CustomField',
            },
            fieldName: 'customField',
            idFieldName: 'idCustomField',
          },
          cache,
        );
      }
    }

    const value = readField<TrelloCustomFieldItemValueInfo>('value', incoming);
    if (value) {
      const idValue = readField<string | null>('id', value);
      /**
       * We should instead eventually create a
       * syncTrelloCustomFieldItemValueInfoToCustomFieldItemValueInfo
       * that will sync the scalar fields for the value object
       * when we get a reference.
       */
      if (isString(idValue)) {
        // This is a dropdown custom field and we want to avoid writing to any
        // fields inside CustomFieldItem.value, we just want it to be null
        syncNativeNestedObjectToRest(
          customFieldItem,
          dropdownValueFieldMappings,
          generateDropdownCustomFieldItemValueFragment,
          generateDropdownCustomFieldItemValueData,
          // We've got a stupid server-side bug here where we created an id field on
          // TrelloCustomFieldItemValueInfo but didn't make it an ARI
          // So instead of this being a StoreObject it's a Reference that looks like this
          // { __ref: 'TrelloCustomFieldItemValueInfo:67db1fa314eb7019547c5a3a' }
          // Since syncNativeNestedObjectToRest relies on Object.keys(), having a ref
          // messes up the nested object syncing. So we pass through our own StoreObject
          { __typename: 'CustomFieldItem', id: idValue },
          cache,
        );
      } else {
        // This is a number/text/date/checkbox custom field and idValue should be null
        syncNativeNestedObjectToRest(
          customFieldItem,
          valueFieldMappings,
          generateCustomFieldItemValueFragment,
          generateCustomFieldItemValueData,
          value,
          cache,
        );
      }
    }
  } catch (err) {
    sendErrorEvent(err);
  }
};
